import { useMemo, useState, type ReactNode } from 'react';
import { Lightbulb, MessageSquareText } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Fact, QuietEmpty, StoredImage, WorkerChip } from './shared';
import { useRespondNote, useWorkNotes } from '@/features/workMode/hooks';
import { describeWorkError } from '@/features/workMode/api';
import { NOTE_KIND_LABEL, NOTE_STATUS_LABEL, NOTE_STATUS_VARIANT } from '@/features/workMode/labels';
import { NOTE_KINDS, NOTE_STATUSES, type WorkNote, type WorkNoteKind, type WorkNoteStatus } from '@/features/workMode/types';
import type { WorkLookups } from '@/features/workMode/useLookups';
import { formatDateTime, formatRelative } from '@/lib/formatters';

interface FeedbackTabProps {
  lookups: WorkLookups;
  canManage: boolean;
}

type StatusFilter = 'open' | 'done' | 'all';
type KindFilter = WorkNoteKind | 'all';

const KIND_VARIANT: Record<WorkNoteKind, 'warning-soft' | 'info-soft' | 'success-soft' | 'outline' | 'warning' | 'muted'> = {
  issue: 'warning-soft',
  suggestion: 'info-soft',
  tool_request: 'outline',
  instruction_change: 'info-soft',
  safety: 'warning',
  kudos: 'success-soft',
};

/**
 * The voice of the bench: problems, ideas, tool requests, instruction fixes,
 * safety flags and shout-outs. Every item gets a status and a reply so
 * people can see that what they said went somewhere.
 */
export function FeedbackTab({ lookups, canManage }: FeedbackTabProps) {
  const [status, setStatus] = useState<StatusFilter>('open');
  const [kind, setKind] = useState<KindFilter>('all');
  const { data: notes = [], isLoading } = useWorkNotes(status === 'done' ? 'done' : status);
  const [selected, setSelected] = useState<WorkNote | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const list = useMemo(() => (kind === 'all' ? notes : notes.filter((n) => n.kind === kind)), [notes, kind]);
  const counts = useMemo(() => {
    const m = new Map<WorkNoteKind, number>();
    for (const n of notes) m.set(n.kind, (m.get(n.kind) ?? 0) + 1);
    return m;
  }, [notes]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          <Chip active={kind === 'all'} onClick={() => setKind('all')}>
            All ({notes.length})
          </Chip>
          {NOTE_KINDS.map((k) => (
            <Chip key={k.value} active={kind === k.value} onClick={() => setKind(k.value)}>
              {k.label}
              {counts.get(k.value) ? ` (${counts.get(k.value)})` : ''}
            </Chip>
          ))}
        </div>
        <div className="flex gap-1.5">
          {(['open', 'done', 'all'] as StatusFilter[]).map((s) => (
            <Chip key={s} active={status === s} onClick={() => setStatus(s)}>
              {s === 'open' ? 'Needs a reply' : s === 'done' ? 'Done' : 'Everything'}
            </Chip>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <QuietEmpty>
          <Lightbulb className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          {status === 'open' ? 'Everything from the floor has been answered.' : 'No notes match these filters.'}
        </QuietEmpty>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {list.map((n) => (
            <li key={n.id}>
              <NoteCard note={n} lookups={lookups} onOpen={() => setSelected(n)} />
            </li>
          ))}
        </ul>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent>
          {selected && (
            <NoteDetail key={selected.id} note={selected} lookups={lookups} canManage={canManage} onClose={() => setSelected(null)} onPhoto={setLightbox} />
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {lightbox && <StoredImage bucket="work-captures" path={lightbox} alt="Note photo" className="max-h-[80vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-sm transition-colors',
        active ? 'border-info bg-info-soft/30 text-info' : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      {children}
    </button>
  );
}

function NoteCard({ note: n, lookups, onOpen }: { note: WorkNote; lookups: WorkLookups; onOpen: () => void }) {
  return (
    <article className={cn('rounded-2xl border bg-card p-4', n.kind === 'safety' && n.status === 'new' ? 'border-fresh-salmon' : 'border-border')}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={KIND_VARIANT[n.kind]}>{NOTE_KIND_LABEL[n.kind]}</Badge>
          <Badge variant={NOTE_STATUS_VARIANT[n.status]}>{NOTE_STATUS_LABEL[n.status]}</Badge>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{formatRelative(n.created_at)}</span>
      </div>
      <button type="button" onClick={onOpen} className="mt-2 block w-full text-left">
        <p className="line-clamp-3 whitespace-pre-line text-sm text-foreground">{n.body}</p>
      </button>
      <div className="mt-3 flex items-center justify-between gap-3">
        <WorkerChip
          person={lookups.person(n.user_id)}
          size={24}
          sub={[lookups.stationName(n.workstation_id), lookups.itemName(n.item_id), n.step_number ? `step ${n.step_number}` : null].filter(Boolean).join(' · ') || undefined}
        />
        {(n.photo_paths ?? []).length > 0 && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {n.photo_paths.length} photo{n.photo_paths.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {n.manager_response && (
        <div className="mt-3 rounded-xl bg-secondary/60 px-3 py-2 text-sm text-foreground">
          <MessageSquareText className="mr-1.5 inline h-3.5 w-3.5 text-muted-foreground" />
          {n.manager_response}
        </div>
      )}
    </article>
  );
}

function NoteDetail({
  note: n,
  lookups,
  canManage,
  onClose,
  onPhoto,
}: {
  note: WorkNote;
  lookups: WorkLookups;
  canManage: boolean;
  onClose: () => void;
  onPhoto: (path: string) => void;
}) {
  const respond = useRespondNote();
  const [status, setStatus] = useState<WorkNoteStatus>(n.status === 'new' ? 'acknowledged' : n.status);
  const [response, setResponse] = useState(n.manager_response ?? '');

  const save = () => {
    respond.mutate(
      { id: n.id, status, response: response.trim() || null },
      {
        onSuccess: () => {
          toast.success('Reply saved — it shows up on their next sign-in.');
          onClose();
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle>{NOTE_KIND_LABEL[n.kind]}</SheetTitle>
        <SheetDescription>
          {formatDateTime(n.created_at)} · {lookups.stationName(n.workstation_id) ?? 'No station'}
        </SheetDescription>
      </SheetHeader>
      <SheetBody className="space-y-5">
        <WorkerChip person={lookups.person(n.user_id)} sub={n.item_id ? `While building ${lookups.itemName(n.item_id)}` : undefined} size={40} />
        <p className="whitespace-pre-line text-sm text-foreground">{n.body}</p>
        {(n.photo_paths ?? []).length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {n.photo_paths.map((p) => (
              <StoredImage key={p} bucket="work-captures" path={p} alt="Note photo" className="aspect-square w-full" onClick={() => onPhoto(p)} />
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Fact label="Status">
            <Badge variant={NOTE_STATUS_VARIANT[n.status]}>{NOTE_STATUS_LABEL[n.status]}</Badge>
          </Fact>
          <Fact label="About step">{n.step_number ? `Step ${n.step_number}` : '—'}</Fact>
        </div>
        {n.responded_at && (
          <p className="text-xs text-muted-foreground">
            Last reply {formatRelative(n.responded_at)} by {lookups.personName(n.responded_by)}.
          </p>
        )}
        {canManage && (
          <div className="space-y-3 rounded-xl border border-border p-4">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as WorkNoteStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="note-response">Reply</Label>
              <Textarea id="note-response" value={response} onChange={(e) => setResponse(e.target.value)} rows={4} placeholder="What happens next, and thank them." />
            </div>
          </div>
        )}
      </SheetBody>
      {canManage && (
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={save} disabled={respond.isPending}>
            Save reply
          </Button>
        </SheetFooter>
      )}
    </>
  );
}
